import { round } from 'lodash';
import { FC, useMemo } from 'react';
import { EnergyDataTrend } from '@demind-inc/core';
import { Stack, Typography } from '@mui/material';
import './EnergyDurationCard.scss';
import { East, NorthEast, SouthEast } from '@mui/icons-material';
import { useEnergyTrend } from '../../../../../hooks';
import { useTranslation } from 'react-i18next';
import { formatDurationHoursMinutes, formatMinutes } from '../../../i18n';

interface EnergyDurationCardProps {
  className?: string;
  energyDailyTrend: EnergyDataTrend;
  highEnergyDurationDiff?: number | null;
  highEnergyDurationDiffColor?: string;
  weeklyHighEnergyDuration?: number;
  weeklyHighEnergyDurationDiff?: number | null;
  weeklyHighEnergyDurationDiffColor?: string;
  isSimplified?: boolean;
}

const EnergyDurationCard: FC<EnergyDurationCardProps> = ({
  className,
  energyDailyTrend,
  highEnergyDurationDiff,
  highEnergyDurationDiffColor,
  weeklyHighEnergyDuration,
  weeklyHighEnergyDurationDiff,
  weeklyHighEnergyDurationDiffColor,
  isSimplified = false,
}) => {
  const { t } = useTranslation();
  const { dataTrendPeriod } = useEnergyTrend();

  const isWeekly = dataTrendPeriod === 'weekly';
  const energyData = isWeekly
    ? { highEnergyDurationMin: weeklyHighEnergyDuration }
    : energyDailyTrend;
  const durationDiff = isWeekly ? weeklyHighEnergyDurationDiff : highEnergyDurationDiff;
  const durationDiffColor = isWeekly
    ? weeklyHighEnergyDurationDiffColor
    : highEnergyDurationDiffColor;

  const highEnergyDurationString = useMemo(() => {
    if (!energyData?.highEnergyDurationMin) {
      return t('data.common.emptyDuration');
    }
    const hours = Math.floor(energyData.highEnergyDurationMin / 60);
    const minutes = energyData.highEnergyDurationMin % 60;
    return formatDurationHoursMinutes(t, hours, minutes);
  }, [energyData?.highEnergyDurationMin, t]);

  if (isSimplified) {
    return (
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body1" className="energy-duration-card__value-title">
          {isWeekly
            ? t('data.circadian.avgHighEnergyDuration')
            : t('data.circadian.highEnergyDuration')}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="body1" className="energy-duration-card__value">
            {highEnergyDurationString}
          </Typography>
          {typeof durationDiff === 'number' && durationDiff !== null && durationDiff !== 0 && (
            <Typography
              variant="body1"
              className="energy-duration-card__value"
              style={{
                color: durationDiffColor,
              }}
            >
              {durationDiff > 0 ? (
                <>
                  <NorthEast className="energy-duration-card__icon" />
                  {formatMinutes(t, durationDiff)}
                </>
              ) : (
                <>
                  <SouthEast className="energy-duration-card__icon" />{' '}
                  {formatMinutes(t, durationDiff)}
                </>
              )}
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  }
  return (
    <Stack flex={1} direction="row" justifyContent="space-between" alignItems="end" gap={3}>
      <Stack flex={1}>
        <Typography variant="h2" className="energy-duration-card__energy-value">
          {highEnergyDurationString}
        </Typography>
        <Typography variant="body2" className="energy-duration-card__energy-unit">
          {isWeekly
            ? t('data.circadian.avgHighEnergyDuration')
            : t('data.circadian.highEnergyDuration')}
        </Typography>
      </Stack>
      {typeof durationDiff === 'number' && durationDiff !== null && (
        <Stack flex={1} alignItems="flex-start" >
          <Typography
            className="energy-duration-card__diff"
            variant="body1"
            sx={{ color: durationDiffColor }}
          >
            {durationDiff > 0 ? (
              <>
                <NorthEast className="energy-duration-card__diff__icon" />
              </>
            ) : durationDiff < 0 ? (
              <>
                <SouthEast className="energy-duration-card__diff__icon" />
              </>
            ) : durationDiff === 0 ? (
              <>
               -
              </>
            ) : (
              ''
            )}
            {formatMinutes(t, durationDiff)}
          </Typography>
          <Typography className="energy-duration-card__diff-label" variant="body2">
            {dataTrendPeriod === 'daily'
              ? t('data.common.vsPrevDay')
              : t('data.common.vsPrevWeek')}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

export default EnergyDurationCard;
